import type { Activity } from '@/types/api'
import type { ReactNode } from 'react'
import {
  Eye,
  FileText,
  LayoutTemplate,
  Package,
  Settings,
  ShoppingBag,
  Star,
  UserPlus,
} from 'lucide-react'

const humanizeAction = (action: string): string =>
  action
    .split('_')
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(' ')

const getStringDetail = (activity: Activity, key: string): string | null => {
  const value = activity.details?.[key]
  return typeof value === 'string' && value.trim().length > 0 ? value : null
}

export const getActivityTargetPath = (activity: Activity): string | null => {
  switch (activity.action) {
    case 'order_created':
    case 'order_status_changed':
      return activity.resource_id ? `/orders/${activity.resource_id}` : '/orders'
    case 'user_registered':
    case 'role_changed':
      return activity.resource_id ? `/users/${activity.resource_id}` : '/users'
    case 'product_created':
    case 'product_updated':
      return activity.resource_id ? `/products/${activity.resource_id}` : '/products'
    case 'create_hero_slide':
    case 'update_hero_slide':
    case 'delete_hero_slide':
      return '/hero-slides'
    case 'create_static_page':
    case 'update_static_page':
    case 'delete_static_page':
      return '/static-pages'
    case 'review_submitted':
      return '/reviews'
    case 'create_testimonial':
    case 'update_testimonial':
    case 'delete_testimonial':
      return '/testimonials'
    default:
      return null
  }
}

export const getActivityIcon = (action: string): ReactNode => {
  switch (action) {
    case 'order_created':
      return <ShoppingBag className="h-4 w-4" />
    case 'user_registered':
      return <UserPlus className="h-4 w-4" />
    case 'product_created':
    case 'product_updated':
      return <Package className="h-4 w-4" />
    case 'review_submitted':
      return <Star className="h-4 w-4" />
    case 'create_testimonial':
    case 'update_testimonial':
    case 'delete_testimonial':
      return <Star className="h-4 w-4" />
    case 'create_hero_slide':
    case 'update_hero_slide':
    case 'delete_hero_slide':
      return <LayoutTemplate className="h-4 w-4" />
    case 'create_static_page':
    case 'update_static_page':
    case 'delete_static_page':
      return <FileText className="h-4 w-4" />
    case 'order_status_changed':
    case 'admin_invited':
    case 'admin_invitation_resent':
    case 'admin_invitation_accepted':
    case 'admin_invitation_cancelled':
    case 'role_changed':
    case 'security_login_failed':
    case 'security_password_changed':
    case 'security_two_factor_enabled':
      return <Settings className="h-4 w-4" />
    default:
      return <Eye className="h-4 w-4" />
  }
}

export const getActivityColor = (action: string): string => {
  switch (action) {
    case 'order_created':
    case 'order_status_changed':
      return 'bg-blue-100 text-blue-800'
    case 'user_registered':
      return 'bg-green-100 text-green-800'
    case 'product_created':
    case 'product_updated':
      return 'bg-purple-100 text-purple-800'
    case 'review_submitted':
      return 'bg-yellow-100 text-yellow-800'
    case 'create_testimonial':
    case 'update_testimonial':
    case 'delete_testimonial':
      return 'bg-pink-100 text-pink-800'
    case 'create_hero_slide':
    case 'update_hero_slide':
    case 'delete_hero_slide':
      return 'bg-cyan-100 text-cyan-800'
    case 'create_static_page':
    case 'update_static_page':
    case 'delete_static_page':
      return 'bg-indigo-100 text-indigo-800'
    case 'admin_invited':
      return 'bg-blue-100 text-blue-800'
    case 'admin_invitation_accepted':
    case 'security_two_factor_enabled':
      return 'bg-green-100 text-green-800'
    case 'admin_invitation_resent':
    case 'security_password_changed':
      return 'bg-orange-100 text-orange-800'
    case 'admin_invitation_cancelled':
    case 'security_login_failed':
      return 'bg-red-100 text-red-800'
    case 'role_changed':
      return 'bg-purple-100 text-purple-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

export const getActivityDescription = (activity: Activity): string => {
  switch (activity.action) {
    case 'order_created':
      return `New order #${getStringDetail(activity, 'order_number') ?? activity.resource_id ?? 'unknown'} placed`
    case 'user_registered':
      return `New user ${getStringDetail(activity, 'user_name') ?? getStringDetail(activity, 'email') ?? 'registered'}`
    case 'product_created':
      return `Product "${getStringDetail(activity, 'product_name') ?? 'Untitled product'}" added to inventory`
    case 'product_updated':
      return `Product "${getStringDetail(activity, 'product_name') ?? 'Untitled product'}" updated`
    case 'review_submitted':
      return `${getStringDetail(activity, 'rating') ?? 'New'} review submitted`
    case 'create_testimonial':
      return `Testimonial created${getStringDetail(activity, 'name') ? `: ${getStringDetail(activity, 'name')}` : ''}`
    case 'update_testimonial':
      return `Testimonial updated${getStringDetail(activity, 'name') ? `: ${getStringDetail(activity, 'name')}` : ''}`
    case 'delete_testimonial':
      return `Testimonial removed${getStringDetail(activity, 'name') ? `: ${getStringDetail(activity, 'name')}` : ''}`
    case 'order_status_changed':
      return `Order status changed to ${getStringDetail(activity, 'new_status') ?? 'a new state'}`
    case 'create_hero_slide':
      return `Hero slide "${getStringDetail(activity, 'title') ?? 'Untitled slide'}" created`
    case 'update_hero_slide':
      return `Hero slide updated${getStringDetail(activity, 'title') ? `: ${getStringDetail(activity, 'title')}` : ''}`
    case 'delete_hero_slide':
      return `Hero slide removed${getStringDetail(activity, 'title') ? `: ${getStringDetail(activity, 'title')}` : ''}`
    case 'create_static_page':
      return `Static page "${getStringDetail(activity, 'title') ?? getStringDetail(activity, 'page_title') ?? 'Untitled page'}" created`
    case 'update_static_page':
      return `Static page updated${getStringDetail(activity, 'title') ? `: ${getStringDetail(activity, 'title')}` : ''}`
    case 'delete_static_page':
      return `Static page removed${getStringDetail(activity, 'title') ? `: ${getStringDetail(activity, 'title')}` : ''}`
    case 'admin_invited':
      return 'Admin invited'
    case 'admin_invitation_accepted':
      return 'Admin invitation accepted'
    case 'admin_invitation_resent':
      return 'Admin invitation resent'
    case 'admin_invitation_cancelled':
      return 'Admin invitation cancelled'
    case 'role_changed':
      return `User role changed from ${getStringDetail(activity, 'old_role') ?? 'unknown'} to ${getStringDetail(activity, 'new_role') ?? 'unknown'}`
    case 'security_login_failed':
      return 'Login failed'
    case 'security_password_changed':
      return 'Password changed'
    case 'security_two_factor_enabled':
      return 'Two-factor authentication enabled'
    default:
      return activity.action_display || humanizeAction(activity.action)
  }
}
